import React, {
  createContext,
  use,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  getOnlineStatus,
  addOnlineListener,
  addPendingChangeListener,
  syncPendingChanges,
  performFullSync,
  checkConnectivity,
  miseAJourLocale,
} from '../lib/api';
import { activerPortee } from '../lib/offline/portee';
import { quandInactif } from '../lib/offline/arrierePlan';
import { useAuth } from './AuthContext';
import { syncQueue, fullSync } from '../lib/db';
import { isDesktop, signalerEcrituresEnAttente } from '../lib/desktop';

const SyncContext = createContext(null);

export const useSync = () => {
  const context = use(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
};

// Variante tolérante: hors fournisseur (écrans isolés, tests), on se croit en ligne.
export const useSyncOuEnLigne = () => use(SyncContext) || { isOnline: true };

export const SyncProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(getOnlineStatus());
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(() => null);
  const [syncError, setSyncError] = useState(null);
  const [failedCount, setFailedCount] = useState(0);

  const triggerSyncRef = useRef(null);
  const { isAuthenticated, user } = useAuth();

  // Données sur l'appareil: téléchargement après la connexion, puis mises à jour
  // régulières, au retour sur l'onglet et au retour du réseau (en silence).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let annule = false;
    // Le premier écran passe avant: le téléchargement ne démarre qu'une fois la
    // page inactive, et ne se met jamais en travers des requêtes de l'écran.
    let annulerDemarrage = null;
    activerPortee(user).then(() => {
      if (!annule) annulerDemarrage = quandInactif(() => miseAJourLocale());
    });
    // Application Windows: la fenêtre réduite continue de se tenir à jour (la
    // coque ne ralentit pas ses minuteries), pour que tout soit prêt au retour.
    const minuterie = setInterval(() => {
      if (document.visibilityState === 'visible' || isDesktop()) miseAJourLocale();
    }, 60000);
    const auRetourSurLOnglet = () => {
      if (document.visibilityState === 'visible') miseAJourLocale();
    };
    document.addEventListener('visibilitychange', auRetourSurLOnglet);
    return () => {
      annule = true;
      if (annulerDemarrage) annulerDemarrage();
      clearInterval(minuterie);
      document.removeEventListener('visibilitychange', auRetourSurLOnglet);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.user_id, user?.org_id]);

  const refreshCounts = useCallback(async () => {
    try {
      // Indexed counts, not a full table scan: the queue can hold thousands of items.
      const enAttente = await syncQueue.countPending();
      setPendingCount(enAttente);
      // La coque Windows ne redémarre jamais pour une mise à jour tant qu'il en reste.
      signalerEcrituresEnAttente(enAttente);
      const failed = await syncQueue.getFailed();
      setFailedCount(failed.length);
    } catch (err) {
      console.error('Failed to refresh sync counts:', err);
    }
  }, []);

  // Load pending count and last sync time
  useEffect(() => {
    const loadState = async () => {
      await refreshCounts();
      try {
        const lastSync = await fullSync.getLastSyncTime();
        setLastSyncTime(lastSync);
      } catch (err) {
        console.error('Failed to load sync state:', err);
      }
    };

    loadState();

    // Safety net; the real-time updates come from the pending-change listener below.
    const interval = setInterval(loadState, 10000);
    // Immediate refresh whenever a mutation is queued or the queue is drained.
    const unsubscribe = addPendingChangeListener(refreshCounts);

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [refreshCounts]);

  // Trigger manual sync
  const triggerSync = useCallback(async () => {
    if (isSyncing) return;
    // A manual click while flagged offline is also a request to re-check the server:
    // navigator.onLine may have been lying (captive portal, dead backend).
    if (!isOnline && !(await checkConnectivity())) return;

    setIsSyncing(true);
    setSyncError(null);

    try {
      // First, push pending changes
      const pushResult = await syncPendingChanges();

      if (!pushResult.success && pushResult.error) {
        setSyncError(true);
      }

      // Un conflit ramène l'écran à la version du serveur, sans bruit: le refus
      // lui-même a déjà été dit par le serveur à l'écran concerné.
      if (pushResult.success && pushResult.conflicts?.length > 0) {
        console.warn('Conflicting changes dropped:', pushResult.conflicts.length);
      }

      // Then, pull latest data
      const lastSync = await fullSync.getLastSyncTime();
      const pullResult = await performFullSync(lastSync);

      if (pullResult.success) {
        setLastSyncTime(
          pullResult.data?.server_timestamp || new Date().toISOString(),
        );
      } else if (pullResult.error) {
        setSyncError(true);
      }

      await refreshCounts();
    } catch (err) {
      console.error('Sync failed:', err);
      setSyncError(true);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, isOnline, refreshCounts]);

  // Keep latest triggerSync available without re-subscribing online listener
  useEffect(() => {
    triggerSyncRef.current = triggerSync;
  }, [triggerSync]);

  // Listen for online/offline changes
  useEffect(() => {
    const unsubscribe = addOnlineListener((online) => {
      setIsOnline(online);
      if (online) {
        // Auto-sync when coming back online
        Promise.resolve(triggerSyncRef.current?.()).finally(() => miseAJourLocale());
      }
    });

    return unsubscribe;
  }, []);

  // Force full refresh (clears local data and re-syncs)
  const forceFullSync = useCallback(async () => {
    if (isSyncing || !isOnline) return;

    setIsSyncing(true);
    setSyncError(null);

    try {
      const result = await performFullSync();
      if (result.success) {
        setLastSyncTime(
          result.data?.server_timestamp || new Date().toISOString(),
        );
        await refreshCounts();
      } else {
        setSyncError(true);
      }
    } catch (err) {
      console.error('Full sync failed:', err);
      setSyncError(true);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, isOnline, refreshCounts]);

  // Increment pending count (called when offline mutation is queued)
  const incrementPending = useCallback(() => {
    setPendingCount((prev) => prev + 1);
  }, []);

  const value = {
    isOnline,
    isSyncing,
    pendingCount,
    failedCount,
    lastSyncTime,
    syncError,
    triggerSync,
    forceFullSync,
    incrementPending,
    refreshCounts,
  };

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
};

export default SyncContext;
